// ================================================
// Paleta de cores de quadros e colunas
// ================================================
//
// Board.color e BoardColumn.color guardam só o NOME de uma cor da paleta Tailwind (ex.: 'sky').
// A tela monta as classes em runtime (`bg-${name}-100`), que o JIT do Tailwind não consegue
// descobrir varrendo o código. Por isso:
//   1. o cadastro só aceita nomes desta lista (seletor de paleta, nunca texto livre);
//   2. o tailwind.config.js tem um `safelist` com a MESMA lista de nomes, prefixos e tons;
//   3. colors.test.ts lê o tailwind.config.js e falha se as listas divergirem — uma cor nova
//      aqui sem o safelist sumiria do CSS só no build de produção.
//
// Para acrescentar uma cor: inclua o nome aqui E em COLOR_NAMES do tailwind.config.js.

export const COLOR_NAMES = [
  'slate',
  'gray',
  'red',
  'orange',
  'amber',
  'yellow',
  'lime',
  'green',
  'emerald',
  'teal',
  'cyan',
  'sky',
  'blue',
  'indigo',
  'violet',
  'purple',
  'fuchsia',
  'pink',
  'rose',
] as const;

export type ColorName = (typeof COLOR_NAMES)[number];

export const COLOR_LABELS: Record<ColorName, string> = {
  slate: 'Chumbo',
  gray: 'Cinza',
  red: 'Vermelho',
  orange: 'Laranja',
  amber: 'Âmbar',
  yellow: 'Amarelo',
  lime: 'Lima',
  green: 'Verde',
  emerald: 'Esmeralda',
  teal: 'Turquesa',
  cyan: 'Ciano',
  sky: 'Azul claro',
  blue: 'Azul',
  indigo: 'Índigo',
  violet: 'Violeta',
  purple: 'Roxo',
  fuchsia: 'Fúcsia',
  pink: 'Rosa',
  rose: 'Rosé',
};

export const DEFAULT_COLOR: ColorName = 'slate';

/** Prefixos e tons cobertos pelo safelist (precisam bater com o tailwind.config.js). */
export const COLOR_PREFIXES = ['bg', 'text', 'border', 'border-t', 'border-l', 'ring'] as const;
export const COLOR_SHADES = ['50', '100', '200', '300', '500', '600', '700', '800'] as const;

export interface ColorClasses {
  /** Bolinha/amostra sólida (seletor, avatar de quadro). */
  swatch: string;
  /** Fundo suave (cabeçalho de coluna, chip). */
  soft: string;
  /** Fundo intermediário (barra do cronograma). */
  bar: string;
  /** Texto legível sobre o fundo suave. */
  text: string;
  /** Texto forte sobre a barra. */
  textStrong: string;
  /** Borda suave. */
  border: string;
  /** Borda superior grossa da coluna do kanban. */
  borderTop: string;
  /** Faixa lateral do card de quadro. */
  borderLeft: string;
  /** Anel de seleção (seletor de cor). */
  ring: string;
}

export const isValidColor = (color: string | undefined | null): color is ColorName =>
  !!color && (COLOR_NAMES as readonly string[]).includes(color);

/** Cor de exibição, com fallback para valores desconhecidos/legados. */
export const colorOrDefault = (color: string | undefined | null): ColorName =>
  isValidColor(color) ? color : DEFAULT_COLOR;

/** Classes Tailwind de uma cor da paleta (todas cobertas pelo safelist). */
export const colorClasses = (color: string | undefined | null): ColorClasses => {
  const c = colorOrDefault(color);
  return {
    swatch: `bg-${c}-500`,
    soft: `bg-${c}-100`,
    bar: `bg-${c}-200`,
    text: `text-${c}-700`,
    textStrong: `text-${c}-800`,
    border: `border-${c}-300`,
    borderTop: `border-t-${c}-500`,
    borderLeft: `border-l-${c}-500`,
    ring: `ring-${c}-500`,
  };
};

/** Tom 500 de cada cor da paleta (Tailwind 3.4), para gráficos SVG que não aceitam classes. */
const COLOR_HEX_500: Record<ColorName, string> = {
  slate: '#64748b',
  gray: '#6b7280',
  red: '#ef4444',
  orange: '#f97316',
  amber: '#f59e0b',
  yellow: '#eab308',
  lime: '#84cc16',
  green: '#22c55e',
  emerald: '#10b981',
  teal: '#14b8a6',
  cyan: '#06b6d4',
  sky: '#0ea5e9',
  blue: '#3b82f6',
  indigo: '#6366f1',
  violet: '#8b5cf6',
  purple: '#a855f7',
  fuchsia: '#d946ef',
  pink: '#ec4899',
  rose: '#f43f5e',
};

/** Hex do tom 500 da cor da paleta (fallback para a cor padrão) — usado pelos gráficos (recharts). */
export const colorHex = (color: string | undefined | null): string => COLOR_HEX_500[colorOrDefault(color)];

/** Todas as classes que a paleta pode gerar — usadas no teste contra o safelist. */
export const ALL_COLOR_CLASSES: string[] = COLOR_NAMES.flatMap((c) => Object.values(colorClasses(c)));
